import { useState } from 'react';
import {
  App,
  Button,
  Checkbox,
  DatePicker,
  Drawer,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Table,
  Tag,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import type { Cookie, SameSite } from '@core/entities/Cookie';
import type { CookiePayload } from '@core/repositories/CookieRepository';
import {
  useClearCookies,
  useCookies,
  useCreateCookie,
  useDeleteCookie,
  useUpdateCookie,
} from '@presentation/hooks/useCookies';

interface CookieJarDrawerProps {
  workspaceId: string;
  open: boolean;
  onClose: () => void;
}

interface CookieFormValues {
  domain: string;
  name: string;
  value: string;
  path: string;
  expiresAt: Dayjs | null;
  isHttpOnly: boolean;
  isSecure: boolean;
  sameSite: SameSite | null;
}

const SAME_SITE_OPTIONS: { label: string; value: SameSite }[] = [
  { label: 'Lax', value: 'lax' },
  { label: 'Strict', value: 'strict' },
  { label: 'None', value: 'none' },
];

export const CookieJarDrawer = ({ workspaceId, open, onClose }: CookieJarDrawerProps) => {
  const { message } = App.useApp();
  const { data: cookies, isLoading } = useCookies(workspaceId);
  const createCookie = useCreateCookie(workspaceId);
  const updateCookie = useUpdateCookie(workspaceId);
  const deleteCookie = useDeleteCookie(workspaceId);
  const clearCookies = useClearCookies(workspaceId);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Cookie | null>(null);
  const [form] = Form.useForm<CookieFormValues>();

  // The Modal uses destroyOnHidden, so its <Form> only exists in the DOM
  // while open — the form instance is disconnected before that, which is
  // why initial values are passed via `initialValues` on mount (computed
  // here) rather than pushed imperatively via `form.setFieldsValue()`.
  const initialValues: CookieFormValues = editing
    ? {
        domain: editing.domain,
        name: editing.name,
        value: editing.value,
        path: editing.path,
        expiresAt: editing.expiresAt ? dayjs(editing.expiresAt) : null,
        isHttpOnly: editing.isHttpOnly,
        isSecure: editing.isSecure,
        sameSite: editing.sameSite,
      }
    : {
        domain: '',
        name: '',
        value: '',
        path: '/',
        expiresAt: null,
        isHttpOnly: false,
        isSecure: false,
        sameSite: 'lax',
      };

  const openCreateForm = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEditForm = (cookie: Cookie) => {
    setEditing(cookie);
    setFormOpen(true);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const payload: CookiePayload = {
      domain: values.domain,
      name: values.name,
      value: values.value,
      path: values.path || '/',
      expiresAt: values.expiresAt ? values.expiresAt.toISOString() : null,
      isHttpOnly: values.isHttpOnly,
      isSecure: values.isSecure,
      sameSite: values.sameSite,
    };

    if (editing) {
      updateCookie.mutate(
        { id: editing.id, payload },
        {
          onSuccess: () => {
            void message.success('Cookie saved');
            setFormOpen(false);
          },
        },
      );
    } else {
      createCookie.mutate(payload, {
        onSuccess: () => {
          void message.success('Cookie added');
          setFormOpen(false);
        },
      });
    }
  };

  return (
    <Drawer title="Cookie Jar" open={open} onClose={onClose} size={760}>
      <div className="mb-3 flex items-center justify-between">
        <span className="text-xs text-gray-500">
          Cookies received via Set-Cookie on a response are stored here automatically; you can also
          add or edit them manually.
        </span>
        <div className="flex shrink-0 gap-2">
          <Button size="small" icon={<PlusOutlined />} onClick={openCreateForm}>
            Add cookie
          </Button>
          <Popconfirm title="Clear all cookies?" onConfirm={() => clearCookies.mutate()}>
            <Button size="small" danger>
              Clear all
            </Button>
          </Popconfirm>
        </div>
      </div>

      <Table
        size="small"
        loading={isLoading}
        dataSource={cookies}
        rowKey="id"
        pagination={false}
        locale={{ emptyText: <Empty description="No cookies yet" /> }}
        columns={[
          { title: 'Domain', dataIndex: 'domain', width: 180 },
          { title: 'Name', dataIndex: 'name', width: 140 },
          {
            title: 'Value',
            dataIndex: 'value',
            ellipsis: true,
            render: (value: string) => <span className="font-mono text-xs">{value}</span>,
          },
          { title: 'Path', dataIndex: 'path', width: 80 },
          {
            title: 'Flags',
            key: 'flags',
            width: 180,
            render: (_, cookie: Cookie) => (
              <div className="flex flex-wrap gap-1">
                {cookie.isHttpOnly && <Tag>HttpOnly</Tag>}
                {cookie.isSecure && <Tag color="green">Secure</Tag>}
                {cookie.sameSite && <Tag color="blue">{cookie.sameSite}</Tag>}
              </div>
            ),
          },
          {
            title: 'Expires',
            dataIndex: 'expiresAt',
            width: 140,
            render: (value: string | null) =>
              value ? dayjs(value).format('YYYY-MM-DD HH:mm') : 'Session',
          },
          {
            title: '',
            key: 'actions',
            width: 80,
            render: (_, cookie: Cookie) => (
              <div className="flex gap-1">
                <Button
                  size="small"
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => openEditForm(cookie)}
                />
                <Popconfirm
                  title="Delete this cookie?"
                  onConfirm={() => deleteCookie.mutate(cookie.id)}
                >
                  <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </div>
            ),
          },
        ]}
      />

      <Modal
        open={formOpen}
        onCancel={() => setFormOpen(false)}
        onOk={handleSubmit}
        confirmLoading={createCookie.isPending || updateCookie.isPending}
        title={editing ? 'Edit cookie' : 'Add cookie'}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" initialValues={initialValues}>
          <Form.Item
            name="domain"
            label="Domain"
            rules={[{ required: true, message: 'Domain is required' }]}
          >
            <Input placeholder="api.example.com" />
          </Form.Item>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Name is required' }]}
          >
            <Input placeholder="session" />
          </Form.Item>
          <Form.Item
            name="value"
            label="Value"
            rules={[{ required: true, message: 'Value is required' }]}
          >
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="path" label="Path">
            <Input placeholder="/" />
          </Form.Item>
          <Form.Item name="expiresAt" label="Expires (leave empty for a session cookie)">
            <DatePicker showTime className="w-full" />
          </Form.Item>
          <Form.Item name="sameSite" label="SameSite">
            <Select options={SAME_SITE_OPTIONS} allowClear />
          </Form.Item>
          <div className="flex gap-4">
            <Form.Item name="isHttpOnly" valuePropName="checked">
              <Checkbox>HttpOnly</Checkbox>
            </Form.Item>
            <Form.Item name="isSecure" valuePropName="checked">
              <Checkbox>Secure</Checkbox>
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </Drawer>
  );
};
